import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlus, faBolt } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { QualificationTrainingProvider } from '@wilson/interfaces';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';

@Component({
  selector: 'wilson-provider-renderer',
  template: `
    <nz-form-item>
      <nz-form-control>
        @if (isEditable) {
          <nz-select
            nzShowSearch
            nzAllowClear
            [nzCustomTemplate]="multipleTemplate"
            [nzDropdownRender]="renderTemplate"
            [nzDropdownMatchSelectWidth]="false"
            [nzPlaceHolder]="'general.search' | translate"
            [(ngModel)]="selectedProvider"
            (ngModelChange)="onChange($event)"
            [nzDropdownStyle]="{ 'min-width': '300px' }"
          >
            @for (provider of providers; track provider.id) {
              <nz-option
                [nzLabel]="provider.name"
                [nzValue]="provider"
              ></nz-option>
            }
          </nz-select>
        } @else {
          @if (selectedProvider?.providerType === 'digital') {
            <nz-tag nzColor="success" class="mx-1">
              <span class="text-display">
                {{ selectedProvider?.name }}
              </span>
              <fa-icon [icon]="faBolt" class="ms-1"></fa-icon>
            </nz-tag>
          } @else {
            <div class="mx-1">
              {{ selectedProvider?.name }}
            </div>
          }
        }
        <ng-template #multipleTemplate let-selected>
          @if (selected.nzValue.providerType === 'digital') {
            <nz-tag nzColor="success" class="mx-1">
              <span>
                {{ selected.nzLabel }}
              </span>
              <fa-icon [icon]="faBolt" class="ms-1"></fa-icon>
            </nz-tag>
          } @else {
            <div class="ant-select-selection-item-content">
              {{ selected.nzLabel }}
            </div>
          }
        </ng-template>

        <ng-template #renderTemplate>
          <nz-divider></nz-divider>
          <div class="container">
            <input
              type="text"
              nz-input
              [(ngModel)]="newProviderName"
              [placeholder]="
                'page.training-qualifications.provider_placeholder' | translate
              "
            />
            <a
              class="add-item"
              (click)="addProvider()"
              [ngClass]="{ 'disabled-item': newProviderName.length === 0 }"
            >
              <fa-icon [icon]="faPlus"></fa-icon>
              {{ 'page.training-qualifications.add-provider' | translate }}
            </a>
          </div>
        </ng-template>
      </nz-form-control>
    </nz-form-item>
  `,
  styles: [
    `
      .container {
        padding: 8px;
        display: flex;
        gap: 8px;
      }

      .add-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px;
        cursor: pointer;

        &.disabled-item {
          color: rgba(0, 0, 0, 0.25);
          cursor: not-allowed;
          pointer-events: none;

          &:hover {
            background-color: transparent;
          }
        }
      }
    `,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzDividerModule,
    NzInputModule,
    NzFormModule,
    TranslatePipe,
    FontAwesomeModule,
    NzTagModule,
  ],
})
export class ProviderRendererComponent {
  @Input() providers: QualificationTrainingProvider[] = [];
  @Input() set selectedProvider(value: QualificationTrainingProvider | null) {
    this._selectedProvider = value;
  }
  get selectedProvider(): QualificationTrainingProvider | null {
    return this._selectedProvider;
  }
  @Input() index!: number;
  @Input() isEditable = false;
  @Output() providerChange = new EventEmitter<QualificationTrainingProvider>();

  newProviderName = '';
  faPlus = faPlus;
  faBolt = faBolt;

  private _selectedProvider: QualificationTrainingProvider | null = null;

  onChange(provider: QualificationTrainingProvider): void {
    this._selectedProvider = provider;
    this.providerChange.emit(provider);
  }

  addProvider(): void {
    this.providers.push({
      name: this.newProviderName,
      id: null,
      authToken: null,
      qualificationTrainingProviderClasses: [],
      description: '',
      logoUrl: '',
      organizationalUnitQualificationTrainingProviders: [],
      providerType: '',
      websiteUrl: '',
      email: '',
    });
  }
}
